/**
 * Mock Agents Context
 * 
 * Provides mock data for multiple agents (for admin views).
 * This simulates having multiple agents in the system.
 * 
 * In-memory only — no persistence, no backend.
 */

import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { supabase } from "@/integrations/supabase/client";

// --- Type Definitions ---

export interface DeliveryTarget {
  monthlyTarget: number;
  deliveredThisMonth: number;
  cycleStartDate: number; // timestamp
  cycleLengthDays: number;
  guaranteedMinimum?: number; // minimum leads guaranteed per cycle
}

export interface MockAgent {
  id: string;
  name: string;
  email: string;
  company: string;
  vertical: "life_insurance" | "mortgage";
  tier: "Bronze" | "Silver" | "Gold" | "Elite";
  isActive: boolean;
  isDemo: boolean;
  /** True when the agent's auth user has a role in admin_users (admin/sales/account_manager) */
  isInternalUser: boolean;
  performanceScore: number;
  walletBalance: number;
  paidBalance: number;
  promotionalBalance: number;
  activeLeadTypesCount: number;
  licensedStates: string[];
  leadPreferences: {
    leadTypeId: string;
    leadTypeName: string;
    bidValue: number;
    dailyCap: number;
    monthlyCap: number;
    isActive: boolean;
  }[];
  performance: {
    avgResponseTime: number;
    contactRate: number;
    closeRate: number;
  };
  leadsReceivedToday: number;
  leadsReceivedThisMonth: number;
  // Delivery target for pool selection pacing
  deliveryTarget?: DeliveryTarget;
}

// --- Context ---

interface MockAgentsState {
  agents: MockAgent[];
}
interface MockAgentsActions {
  refreshAgents: () => Promise<void>;
  upsertAgent: (agent: MockAgent) => Promise<void>;
  deleteAgent: (agentId: string) => Promise<void>;
}

const MockAgentsContext = createContext<
  { state: MockAgentsState; actions: MockAgentsActions } | undefined
>(undefined);

// --- Provider ---

function mapRowToAgent(row: any, internalUserIds?: Set<string>): MockAgent {
  return {
    id: row.id,
    name: row.name,
    email: row.email ?? "",
    company: row.company ?? "",
    vertical: row.vertical,
    tier: row.tier,
    isActive: row.is_active ?? true,
    isDemo: row.is_demo ?? false,
    isInternalUser: internalUserIds ? internalUserIds.has(row.id) : false,
    performanceScore: row.performance_score ?? 0,
    walletBalance: Number(row.wallet_balance ?? 0),
    paidBalance: Number(row.wallet_balance ?? 0),
    promotionalBalance: 0,
    activeLeadTypesCount: Array.isArray(row.lead_preferences)
      ? row.lead_preferences.filter((p: any) => p?.isActive).length
      : 0,
    licensedStates: row.licensed_states ?? [],
    leadPreferences: Array.isArray(row.lead_preferences) ? row.lead_preferences : [],
    performance: row.performance_metrics ?? {
      avgResponseTime: 0,
      contactRate: 0,
      closeRate: 0,
    },
    leadsReceivedToday: row.leads_received_today ?? 0,
    leadsReceivedThisMonth: row.leads_received_this_month ?? 0,
    deliveryTarget: row.delivery_target ?? undefined,
  };
}

function mapAgentToRow(agent: MockAgent) {
  return {
    id: agent.id,
    name: agent.name,
    email: agent.email,
    company: agent.company,
    vertical: agent.vertical,
    tier: agent.tier,
    is_active: agent.isActive,
    performance_score: agent.performanceScore,
    wallet_balance: agent.walletBalance,
    licensed_states: agent.licensedStates,
    lead_preferences: agent.leadPreferences,
    performance_metrics: agent.performance,
    leads_received_today: agent.leadsReceivedToday,
    leads_received_this_month: agent.leadsReceivedThisMonth,
    delivery_target: agent.deliveryTarget ? JSON.parse(JSON.stringify(agent.deliveryTarget)) : null,
  };
}

export const MockAgentsProvider = ({ children }: { children: ReactNode }) => {
  // SAFETY: Start with empty array — never initialize with mock data to prevent
  // hardcoded values (like leadsReceivedThisMonth: 47) from contaminating real DB
  const [agents, setAgents] = useState<MockAgent[]>([]);

  const refreshAgents = useCallback(async () => {
    const [agentsResult, adminResult] = await Promise.all([
      supabase.from("agents").select("*"),
      supabase.from("admin_users").select("user_id"),
    ]);

    if (agentsResult.error) {
      console.error("[Agents] Failed to load agents:", agentsResult.error);
      return;
    }

    const internalUserIds = new Set<string>(
      (adminResult.data ?? []).map((r) => r.user_id)
    );

    if (agentsResult.data && agentsResult.data.length > 0) {
      setAgents(agentsResult.data.map((row) => mapRowToAgent(row, internalUserIds)));
    }
  }, []);

  const upsertAgent = useCallback(async (agent: MockAgent) => {
    const payload = mapAgentToRow(agent);
    // SAFETY: Never write mock delivery counts to the real DB
    payload.leads_received_today = 0;
    payload.leads_received_this_month = 0;
    payload.delivery_target = null; // Never write mock deliveryTarget (contains fake deliveredThisMonth values)
    const { error } = await supabase.from("agents").upsert([payload]);
    if (error) {
      console.error("[Agents] Failed to upsert agent:", error);
      return;
    }
    await refreshAgents();
  }, [refreshAgents]);

  const deleteAgent = useCallback(async (agentId: string) => {
    const { error } = await supabase.from("agents").delete().eq("id", agentId);
    if (error) {
      console.error("[Agents] Failed to delete agent:", error);
      return;
    }
    await refreshAgents();
  }, [refreshAgents]);

  useEffect(() => {
    refreshAgents();
  }, [refreshAgents]);

  const state: MockAgentsState = { agents };
  const actions: MockAgentsActions = { refreshAgents, upsertAgent, deleteAgent };

  return (
    <MockAgentsContext.Provider value={{ state, actions }}>
      {children}
    </MockAgentsContext.Provider>
  );
};

// --- Hook ---

export const useMockAgents = () => {
  const context = useContext(MockAgentsContext);
  if (context === undefined) {
    throw new Error("useMockAgents must be used within a MockAgentsProvider");
  }
  return {
    /** All agents including demo and internal users */
    agents: context.state.agents,
    /** Only real client agents (excludes demo accounts and admin/sales/AM users) */
    clientAgents: context.state.agents.filter((a) => !a.isDemo && !a.isInternalUser),
    actions: context.actions,
  };
};

export default MockAgentsContext;
